<script setup lang="ts">
import { ref, watch } from 'vue';
import Drawer from 'primevue/drawer';
import { useLocalStorage } from '@vueuse/core';

const props = withDefaults(defineProps<{
  visible?: boolean;
  position?: 'left' | 'right' | 'top' | 'bottom';
  header?: string;
  modal?: boolean;
  closeOnEscape?: boolean;
  dismissable?: boolean;
  showCloseIcon?: boolean;
  baseZIndex?: number;
  autoZIndex?: boolean;
  breakpoints?: Record<string, string>;
  class?: string;
}>(), {
  position: 'right',
  modal: true,
  closeOnEscape: true,
  dismissable: true,
  showCloseIcon: true,
  baseZIndex: 0,
  autoZIndex: true,
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'show': [];
  'hide': [];
  'after-hide': [];
}>();

const isVisible = ref(props.visible);
const startX = ref(0);
const startWidth = ref(0);
const drawerWidth = useLocalStorage('drawerWidth', '30rem');
const isResizing = ref(false);
const rafId = ref<number | null>(null);

function handleResizeStart(e: MouseEvent) {
  e.preventDefault();
  isResizing.value = true;
  startX.value = e.clientX;
  startWidth.value = parseInt(drawerWidth.value);

  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', handleResizeEnd);
}

function handleResize(e: MouseEvent) {
  if (!isResizing.value) return;

  if (rafId.value) {
    cancelAnimationFrame(rafId.value);
  }

  rafId.value = requestAnimationFrame(() => {
    const deltaX = e.clientX - startX.value;
    const newWidth = props.position === 'left'
      ? startWidth.value + deltaX
      : startWidth.value - deltaX;

    const minWidth = 200;
    const maxWidth = window.innerWidth * 0.95;

    drawerWidth.value = `${Math.min(Math.max(newWidth, minWidth), maxWidth)}px`;
  });
}

function handleResizeEnd() {
  isResizing.value = false;
  if (rafId.value) {
    cancelAnimationFrame(rafId.value);
    rafId.value = null;
  }
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', handleResizeEnd);
}

function onHide() {
  emit('after-hide');
}


watch(() => props.visible, (newValue) => {
  isVisible.value = newValue;
});

watch(isVisible, (newValue) => {
  emit('update:visible', newValue);
  if (newValue) {
    emit('show');
  } else {
    emit('hide');
  }
});
</script>

<template>
  <Drawer
    v-model:visible="isVisible"
    :position="position"
    :modal="modal"
    :header="header"
    :closeOnEscape="closeOnEscape"
    :dismissable="dismissable"
    :showCloseIcon="showCloseIcon"
    :baseZIndex="baseZIndex"
    :autoZIndex="autoZIndex"
    :breakpoints="breakpoints"
    class="relative"
    :style="{
      width: drawerWidth,
    }"
    @after-hide="onHide"
  >
    <slot></slot>

    <div
      class="absolute top-0 h-full cursor-ew-resize group"
      :class="position === 'left' ? 'right-0' : '-left-2'"
      @mousedown.stop="handleResizeStart"
    >
      <div class="absolute top-0 w-2 h-full"></div>

      <div
        class="absolute top-1/2 -translate-y-1/2 bg-gray-200 rounded-full p-1 shadow-sm"
        :class="position === 'left' ? '-right-2' : '-left-2'"
      >
        <i class="pi pi-arrows-h text-gray-600 text-sm"></i>
      </div>
    </div>
  </Drawer>
</template>

<style scoped>
.resizable-drawer {
  transition: none !important;
}

.resize-handle {
  position: absolute;
  width: 6px;
  height: 100%;
  top: 0;
  cursor: ew-resize;
  background-color: transparent;
  transition: background-color 0.2s;
  z-index: 1;
}

.resize-handle:hover,
.resize-handle:active {
  background-color: var(--primary-color, rgba(0, 0, 0, 0.1));
  opacity: 0.2;
}

.resize-handle.left {
  right: 0;
}

.resize-handle.right {
  left: 0;
}

.resizable-drawer:hover .resize-handle {
  opacity: 0.1;
}
</style>
